import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import { BranchCode } from "api/risk";
import { BranchStepConfigFactory, createStepConfig } from "features/subscription-process/navigation/Steps/createStepConfig";
import { StepPageWrapper } from "features/subscription-process/navigation/Steps/StepPageWrapper";
import { useStepNavigation } from "features/subscription-process/navigation/Steps/useStepNavigation";
import { GenericLoading } from "components/ui/Loader";
import { MriStepIdentifier } from "../types/enums";
import { MRI_ROUTES } from "../utils/mriRoutes";
import { useRefreshSubscriptionProcess } from "../hooks/useRefreshSubscriptionProcess";
import { CopropertyBuildingsForm } from "./CopropertyBuildingsForm";
import { CopropertyIdentityCard } from "./CopropertyIdentityCard";
import { CopropertyRecapPanel } from "./CopropertyRecapPanel";
import { CopropertyStepLayout } from "./CopropertyStepLayout";
import { useCoproperty } from "./useCoproperty";

type BranchStepConfig = Partial<Record<BranchCode, BranchStepConfigFactory>>;

/**
 * Sous-étape « bâtiments » de la création du dossier MRI (après le questionnaire, dont
 * le bloc environnement est le plus éliminatoire), dans le cadre commun des sous-étapes :
 * fiche et avancement à gauche, la boucle des bâtiments en section à droite, le bouton
 * d'étape enregistre. Requiert une copropriété rattachée. Enregistrés et éligibles, les
 * bâtiments mènent aux compléments puis à la synthèse ; un refus (plus de R+7, bâtiment vide
 * non muré) mène à la sortie /excluded-coverage, avec retour possible ici pour corriger.
 */
export const CopropertyBuildingsPage = () => {
  const { riskId, copropertyId, coproperty, isLoading, updateBuildings } = useCoproperty();
  const { refresh } = useRefreshSubscriptionProcess();
  const navigate = useNavigate();
  const [isSubmitting, setIsSubmitting] = useState(false);

  const hasCoproperty = copropertyId != null;

  useEffect(() => {
    if (!riskId || hasCoproperty) return;

    toast.warn("Rattachez d'abord une copropriété pour décrire ses bâtiments.");
    navigate(`${MRI_ROUTES.copropertyRegistration}/${riskId}`, { replace: true });
  }, [riskId, hasCoproperty, navigate]);

  /** Enregistrement de la boucle, exposé par le formulaire au bouton d'étape. */
  const submitRef = useRef<(() => Promise<void>) | null>(null);
  const registerSubmit = useCallback((submit: () => Promise<void>) => {
    submitRef.current = submit;
  }, []);

  /** Saisie enregistrable (bâtiments complets, surfaces cohérentes), remontée par le formulaire. */
  const [isReady, setIsReady] = useState(false);

  const saveBuildings = useCallback(async () => {
    if (!submitRef.current) return;
    setIsSubmitting(true);
    try {
      await submitRef.current();
    } finally {
      setIsSubmitting(false);
    }
  }, []);

  const branchStepConfig = useMemo<BranchStepConfig>(
    () => ({
      [BranchCode.Mri]: createStepConfig({
        prev: {
          identifier: MriStepIdentifier.CopropertySurvey,
          route: MRI_ROUTES.copropertySurvey,
          label: "Retour au questionnaire",
        },
        next: {
          identifier: MriStepIdentifier.Pricing,
          route: MRI_ROUTES.pricing,
          label: isSubmitting ? "Enregistrement…" : "Enregistrer les bâtiments",
          disabled: isSubmitting || !isReady,
          onClick: saveBuildings,
        },
      }),
    }),
    [isSubmitting, isReady, saveBuildings]
  );
  const { stepConfig, goToNext, goToPrevious } = useStepNavigation(branchStepConfig);

  if (!hasCoproperty) {
    return <GenericLoading isPageCentered />;
  }

  return (
    <div className="mx-auto mt-8 max-w-screen-2xl p-4 md:p-8">
      <StepPageWrapper stepConfig={stepConfig} onNext={goToNext} onPrevious={goToPrevious} containerSize="full">
        {isLoading || !coproperty ? (
          <GenericLoading isPageCentered />
        ) : (
          <CopropertyStepLayout
            title="Bâtiments de la copropriété"
            message="Décrivez chaque bâtiment — les sous-sols comptent pour la moitié de leur surface."
            aside={
              <>
                <CopropertyIdentityCard coproperty={coproperty} />
                <CopropertyRecapPanel
                  coproperty={coproperty}
                  hideIdentity
                  showRetainedArea
                  checklist={[
                    { label: "Copropriété rattachée", status: "done" },
                    { label: "Questionnaire éligible", status: "done" },
                    { label: "Bâtiments en cours", status: "active" },
                  ]}
                />
              </>
            }
          >
            <CopropertyBuildingsForm
              coproperty={coproperty}
              onSave={updateBuildings}
              registerSubmit={registerSubmit}
              onReadyChange={setIsReady}
              // Bâtiments éligibles : le process refait le point, puis les compléments
              // (passables) précèdent la synthèse d'éligibilité.
              onEligibleSaved={async () => {
                await refresh();
                navigate(`${MRI_ROUTES.copropertyComplements}/${riskId}`);
              }}
              onRefused={(reasons) =>
                navigate("/excluded-coverage", {
                  state: {
                    message: "Cette copropriété ne peut pas être assurée en MRI",
                    reasons,
                    coverageType: "MRI",
                    secondaryButtonText: "Revoir les bâtiments",
                    secondaryButtonRoute: `${MRI_ROUTES.copropertyBuildings}/${riskId}`,
                  },
                })
              }
            />
          </CopropertyStepLayout>
        )}
      </StepPageWrapper>
    </div>
  );
};
